/**
 * ConnectTelegram.js
 *
 * Links a Telegram account to a BotSpot user. Reached from the bot's
 * connect link containing ?token=<one-time-token> (10-min TTL).
 *
 * Flow (mirrors ConnectDiscord, consent-gated):
 *  1. Load: if not logged in -> Auth0 login, preserving the token in returnTo.
 *  2. Authenticated: GET /connect/telegram/preview to read the token WITHOUT
 *     consuming it. Shows the user which Telegram handle is asking to be
 *     linked so they can explicitly Approve or Cancel.
 *  3. Approve -> POST /connect/telegram/callback to consume the token and
 *     create the ChannelIdentity.
 *  4. Cancel -> POST /connect/telegram/cancel to consume the token without
 *     linking. Prevents a stolen link from being replayed.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useUser from "../../hooks/useUser";
import "./ConnectTelegram.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function ConnectTelegram() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, getApiToken, loginWithRedirect } = useUser();

  // idle | loading-preview | awaiting-approval | linking | success | expired | cancelled | error
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null); // { channelUsername, channelUserIdHint, expiresAt }

  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  useEffect(() => {
    if (isLoading) return;

    if (!token) {
      setStatus("error");
      setMessage(
        "This connect link is missing its token. Return to Telegram and message the bot again to get a fresh link."
      );
      return;
    }

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: `${location.pathname}${location.search}` },
      });
      return;
    }

    let cancelled = false;
    setStatus("loading-preview");

    (async () => {
      try {
        const apiToken = await getApiToken();
        const response = await fetch(
          `${API_URL}/connect/telegram/preview?token=${encodeURIComponent(token)}`,
          {
            headers: { Authorization: `Bearer ${apiToken}` },
          }
        );
        if (cancelled) return;

        const data = await response.json().catch(() => ({}));

        if (response.status === 200) {
          setPreview(data);
          setStatus("awaiting-approval");
          return;
        }
        if (response.status === 400 || response.status === 409) {
          setStatus("expired");
          setMessage(
            data.error ||
              "This connect link has expired or was already used. Return to Telegram and message the bot again to get a fresh link."
          );
          return;
        }
        setStatus("error");
        setMessage(data.error || `Could not load this connect link (status ${response.status}).`);
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err.message || "Network error while loading connect preview.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getApiToken, loginWithRedirect, token, location.pathname, location.search]);

  const handleApprove = async () => {
    setStatus("linking");
    try {
      const apiToken = await getApiToken();
      const response = await fetch(
        `${API_URL}/connect/telegram/callback?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiToken}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.status === 200) {
        setStatus("success");
        setMessage(
          data.alreadyLinked
            ? "This Telegram account is already connected to BotSpot. You are all set!"
            : "Telegram connected. Head back to Telegram and DM the BotSpot Trading bot to start chatting."
        );
        return;
      }
      if (response.status === 400 || response.status === 409) {
        setStatus("expired");
        setMessage(
          data.error ||
            "This connect link has expired or was already used. Return to Telegram and message the bot again to get a fresh link."
        );
        return;
      }
      setStatus("error");
      setMessage(data.error || `Connection failed (status ${response.status}).`);
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Network error while connecting Telegram.");
    }
  };

  const handleCancel = async () => {
    try {
      const apiToken = await getApiToken();
      await fetch(`${API_URL}/connect/telegram/cancel?token=${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiToken}` },
      });
    } catch {
      // Best-effort cancel; even on network error we still move to the cancelled view.
    }
    setStatus("cancelled");
    setMessage("You cancelled the connection. No link was created.");
  };

  const renderPreviewTarget = () => {
    if (!preview) return null;
    const handle = preview.channelUsername
      ? `@${preview.channelUsername}`
      : preview.channelUserIdHint
        ? `Telegram user ending in ...${preview.channelUserIdHint}`
        : "this Telegram account";
    return handle;
  };

  return (
    <div className="connect-telegram-container">
      <div className="connect-telegram-card">
        <h1 className="connect-telegram-title">Connect your Telegram account</h1>

        {(status === "idle" || status === "loading-preview") && (
          <>
            <p className="connect-telegram-body">Loading connect request...</p>
            <div className="connect-telegram-spinner" aria-hidden="true" />
          </>
        )}

        {status === "awaiting-approval" && preview && (
          <>
            <p className="connect-telegram-body">
              <strong>{renderPreviewTarget()}</strong> wants to connect to your BotSpot account.
            </p>
            <p className="connect-telegram-body connect-telegram-body--muted">
              Approving lets this Telegram account DM the BotSpot Trading bot as you and see your strategies.
              Only approve if you just requested this from Telegram.
            </p>
            <div className="connect-telegram-button-row">
              <button
                type="button"
                className="connect-telegram-cta connect-telegram-cta--primary"
                onClick={handleApprove}
              >
                Approve & Connect
              </button>
              <button
                type="button"
                className="connect-telegram-cta connect-telegram-cta--secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {status === "linking" && (
          <>
            <p className="connect-telegram-body">Connecting...</p>
            <div className="connect-telegram-spinner" aria-hidden="true" />
          </>
        )}

        {status === "success" && (
          <>
            <div className="connect-telegram-checkmark" aria-hidden="true">
              ✓
            </div>
            <p className="connect-telegram-body">{message}</p>
            <button
              type="button"
              className="connect-telegram-cta"
              onClick={() => navigate("/ai-agent")}
            >
              Go to AI Agent
            </button>
          </>
        )}

        {status === "cancelled" && (
          <>
            <p className="connect-telegram-body">{message}</p>
            <button
              type="button"
              className="connect-telegram-cta"
              onClick={() => navigate("/")}
            >
              Back to BotSpot
            </button>
          </>
        )}

        {(status === "expired" || status === "error") && (
          <>
            <p className="connect-telegram-body connect-telegram-body--error">{message}</p>
            <button
              type="button"
              className="connect-telegram-cta"
              onClick={() => navigate("/")}
            >
              Back to BotSpot
            </button>
          </>
        )}
      </div>
    </div>
  );
}
